// ============================================
// PAGE - Dashboard (página principal)
// ============================================

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  MdInventory,
  MdShoppingCart,
  MdReceipt,
  MdSettings,
  MdVideoLibrary,
  MdStorefront,
  MdDescription,
  MdRefresh,
  MdCreditCard,
} from 'react-icons/md';
import { FaHandHoldingDollar, FaWhatsapp, FaCashRegister, FaCalendarCheck, FaCrown } from 'react-icons/fa6';
import { PiChartLineUpBold, PiGauge } from "react-icons/pi";
import { IoRocket, IoStorefrontOutline } from 'react-icons/io5';
import { useDashboardKpis, useAuthStore, useSubscription, usePedidosPendientesCount, useMovimientosPendientesCount } from '@almacenes/core';
import { AnnouncementManager } from './components/AnnouncementManager';
import { openExternalLink } from '../../lib/premium';
import { ProspectoPremiumAnuncio } from '../../components/ProspectoPremiumAnuncio';

const allModules = [
  { name: 'Inventario', icon: MdInventory, href: '/inventario', bgColor: '#E8F4FD', iconColor: '#43BFFF', roles: ['propietario'] },
  { name: 'Venta', icon: MdShoppingCart, href: '/ventas', bgColor: '#E8F4FD', iconColor: '#43BFFF', roles: ['propietario', 'vendedor'] },
  { name: 'Reporte', icon: PiChartLineUpBold, href: '/reporte', bgColor: '#E8F4FD', iconColor: '#43BFFF', roles: ['propietario'] },
  { name: 'Caja 360º', icon: FaCashRegister, href: '/caja-360', bgColor: '#E8F4FD', iconColor: '#43BFFF', roles: ['propietario', 'vendedor'] },
  { name: 'Fiados', icon: FaHandHoldingDollar, href: '/fiados', bgColor: '#E8F4FD', iconColor: '#43BFFF', roles: ['propietario'] },
  { name: 'Detalle Boleta', icon: MdReceipt, href: '/detalle-boleta', bgColor: '#E8F4FD', iconColor: '#43BFFF', roles: ['propietario'] },
  { name: 'Configurar', icon: MdSettings, href: '/configurar', bgColor: '#E8F4FD', iconColor: '#43BFFF', roles: ['propietario'] },
  { name: 'Tutoriales', icon: MdVideoLibrary, href: '/tutoriales', bgColor: '#E8F4FD', iconColor: '#43BFFF', roles: ['propietario', 'vendedor'] },
  { name: 'Ecommerce', icon: MdStorefront, href: '/ecommerce', bgColor: '#E8F4FD', iconColor: '#43BFFF', roles: ['propietario'] },
  { name: 'Contactanos', icon: FaWhatsapp, href: 'https://wa.me/+56920201644?text=Hola+me+interesa+comunicarme+con+almacenes+digitales!', bgColor: '#E8F4FD', iconColor: '#43BFFF', roles: ['propietario', 'vendedor'], isExternalLink: true },
  { name: 'Boleta Electronica', icon: MdDescription, href: 'https://docs.google.com/forms/d/e/1FAIpQLSdsrSJS4YPhH5PvFITDXEHofefy_i-toIXXrTNOX7zORkB3Ow/viewform?usp=sf_link', bgColor: '#E8F4FD', iconColor: '#43BFFF', roles: ['propietario'], isExternalLink: true, requiresNoTaxpayer: true },
  //{{ name: 'Resumen Sii', icon: FaFileInvoiceDollar, href: '/resumen-sii', bgColor: '#E8F4FD', iconColor: '#43BFFF', roles: ['propietario'] },
  { name: 'Mi Negocio Pro', icon: IoStorefrontOutline, href: '/mi-negocio', bgColor: '#A855F7', iconColor: '#FFFFFF', roles: ['propietario'], isSpecial: true },
  { name: 'Sesión de ayuda', icon: FaCalendarCheck, href: 'https://almacenesdigitales.cl/pages/calendly.xhtml', bgColor: '#10B981', iconColor: '#FFFFFF', roles: ['propietario', 'vendedor'], isExternalLink: true, isSpecial: true },
  { name: 'Misiones', icon: IoRocket, href: '/misiones', bgColor: '#FFB84D', iconColor: '#FFFFFF', roles: ['propietario', 'vendedor'] },
  { name: 'Premium', icon: FaCrown, href: '/suscripciones/premium', desktopExternalUrl: 'https://app.almacenesdigitales.cl/suscripciones/premium', bgColor: '#43BFFF', iconColor: '#FFFFFF', roles: ['propietario'], isSpecial: true },
];

export const DashboardPage = () => {
  const [showMisionesModal, setShowMisionesModal] = useState(false);
  const [showKpis, setShowKpis] = useState(false);
  const { data: kpis, isLoading: isLoadingKpis, refetch: refetchKpis } = useDashboardKpis();
  const { data: pedidosPendientes } = usePedidosPendientesCount();
  const { suscripcion } = useSubscription();
  const session = useAuthStore((state) => state.session);
  const navigate = useNavigate();

  const pendingCount = pedidosPendientes ?? kpis?.pendingOrdersKpi?.pendingCount ?? 0;

  const handlePedidosClick = () => {
    if (pendingCount > 0) {
      navigate('/ecommerce?estado=PENDIENTE');
    }
  };

  const handleAprobacionesClick = () => {
    if (pendingAprobacionesCount > 0) {
      navigate('/caja/administracion?tab=aprobaciones');
    }
  };

  // Filtrar módulos según el rol del usuario
  const esPropietario = session?.user?.esPropietario ?? true; // Por defecto propietario si no hay info
  const userRole = esPropietario ? 'propietario' : 'vendedor';
  const { data: movimientosCajaPendientes } = useMovimientosPendientesCount(esPropietario);
  const pendingAprobacionesCount = movimientosCajaPendientes ?? 0;
  const modules = allModules.filter(module => {
    if (!module.roles.includes(userRole)) return false;
    if (module.requiresNoTaxpayer && session?.taxpayer) return false;
    return true;
  });

  useEffect(() => {
    // Aplicar color de fondo al main cuando se monta el componente
    const mainElement = document.querySelector('main');
    if (mainElement) {
      mainElement.style.backgroundColor = '#F3F4F9';
    }

    // Limpiar el estilo cuando se desmonta el componente
    return () => {
      if (mainElement) {
        mainElement.style.backgroundColor = '';
      }
    };
  }, []);

  return (
    <div className="mt-2 px-4 md:px-6 lg:px-8 pb-8">
      {!esPropietario && (
        <div className="mb-6 flex justify-center">
          <div className="bg-white rounded-xl border border-gray-200 inline-block w-full md:w-auto px-4 py-3">
            <div className="flex items-center gap-3">
              <PiGauge className="text-2xl" style={{ color: '#43BFFF' }} />
              <span className="font-semibold" style={{ color: '#11194F' }}>
                Bienvenido a tu dashboard
              </span>
            </div>
            <p className="text-gray-600 mt-2">
              Explora las funcionalidades disponibles para ti.
            </p>
          </div>
        </div>
      )}
      {/* KPIs Section - Diseño Compacto - Solo para propietarios */}
      {esPropietario && (
      <div className="mb-6 flex justify-center">
        <div className="bg-white rounded-xl border border-gray-200 inline-block w-full md:w-auto">
          {/* Header con título y botón refresh */}
          <div className="flex items-center justify-between px-4 py-1 border-b border-gray-100 md:border-0">
            <div className="flex items-center gap-2 md:hidden">
              <button
                onClick={() => setShowKpis(!showKpis)}
                className="flex items-center gap-2"
              >
                <PiGauge className="text-2xl" style={{ color: '#43BFFF' }} />
                <span className="font-semibold" style={{ color: '#11194F' }}>
                  Resumen del día
                </span>
              </button>
            </div>
            
            <div className="hidden md:flex items-center gap-2">
              <PiGauge className="text-2xl" style={{ color: '#43BFFF' }} />
              <span className="font-semibold" style={{ color: '#11194F' }}>
                Resumen del día
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => refetchKpis()}
                disabled={isLoadingKpis}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                title="Actualizar KPIs"
              >
                <MdRefresh 
                  className={`text-xl ${isLoadingKpis ? 'animate-spin' : ''}`}
                  style={{ color: '#43BFFF' }}
                />
              </button>
              
              <button
                onClick={() => setShowKpis(!showKpis)}
                className="md:hidden"
              >
                <svg
                  className={`w-5 h-5 transition-transform ${showKpis ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Contenido de los KPIs */}
          <div className={`px-4 pt-1 pb-4 ${showKpis ? 'block' : 'hidden'} md:block`}>
            <div className="flex flex-wrap justify-center gap-4">
              {isLoadingKpis ? (
                <>
                  {/* Skeleton Loaders */}
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-lg w-full md:w-auto md:min-w-[180px]" style={{ maxWidth: '250px' }}>
                      <div className="w-12 h-12 bg-gray-300 rounded-full flex-shrink-0"></div>
                      <div className="min-w-0 flex-1">
                        <div className="h-3 bg-gray-300 rounded w-16 mb-2"></div>
                        <div className="h-6 bg-gray-300 rounded w-12 mb-1"></div>
                        <div className="h-3 bg-gray-300 rounded w-24"></div>
                      </div>
                    </div>
                  ))}
                </>
              ) : kpis ? (
                <>
              {/* Ventas del día */}
              <div className="flex items-center gap-3 p-3 rounded-lg w-full md:w-auto md:min-w-[180px]" style={{ backgroundColor: '#DCFCE7', maxWidth: '250px' }}>
              <MdShoppingCart className="text-3xl flex-shrink-0" style={{ color: '#22C55E' }} />
              <div className="min-w-0">
                <p className="text-xs text-gray-600 font-medium">Ventas Hoy</p>
                <p className="text-xl font-bold truncate" style={{ color: '#11194F' }}>
                  {kpis.todaySalesKpi.boletasCount}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  ${kpis.todaySalesKpi.totalSalesAmount.toLocaleString('es-CL')}
                </p>
              </div>
            </div>

            {/* Pedidos pendientes */}
            <div 
              onClick={handlePedidosClick}
              className={`flex items-center gap-3 p-3 rounded-lg w-full md:w-auto md:min-w-[180px] transition-all ${
                pendingCount > 0 ? 'cursor-pointer hover:shadow-md hover:scale-105' : ''
              }`}
              style={{ backgroundColor: '#FFF4E6', maxWidth: '250px' }}
              title={pendingCount > 0 ? 'Click para ver pedidos pendientes' : ''}
            >
              <MdReceipt className="text-3xl flex-shrink-0" style={{ color: '#FFB84D' }} />
              <div className="min-w-0">
                <p className="text-xs text-gray-600 font-medium">Pedidos</p>
                <p className="text-xl font-bold" style={{ color: '#11194F' }}>
                  {pendingCount}
                </p>
                <p className="text-xs text-gray-500">Pendientes</p>
              </div>
            </div>

            {/* Aprobaciones de caja pendientes */}
            {esPropietario && (
              <div
                onClick={handleAprobacionesClick}
                className={`flex items-center gap-3 p-3 rounded-lg w-full md:w-auto md:min-w-[180px] transition-all ${
                  pendingAprobacionesCount > 0 ? 'cursor-pointer hover:shadow-md hover:scale-105' : ''
                }`}
                style={{ backgroundColor: '#E8F4FD', maxWidth: '250px' }}
                title={pendingAprobacionesCount > 0 ? 'Click para ver movimientos de caja pendientes' : ''}
              >
                <FaCashRegister className="text-3xl flex-shrink-0" style={{ color: '#43BFFF' }} />
                <div className="min-w-0">
                  <p className="text-xs text-gray-600 font-medium">Caja</p>
                  <p className="text-xl font-bold" style={{ color: '#11194F' }}>
                    {pendingAprobacionesCount}
                  </p>
                  <p className="text-xs text-gray-500">Por aprobar</p>
                </div>
              </div>
            )}

            {/* Productos vencidos */}
            <div
              className="flex items-center gap-3 p-3 rounded-lg w-full md:w-auto md:min-w-[180px] cursor-pointer hover:opacity-80 transition-opacity"
              style={{ backgroundColor: '#FEF2F2', maxWidth: '250px' }}
              onClick={() => navigate('/reporte?tab=alertas')}
              title="Ver alertas de inventario"
            >
              <MdInventory className="text-3xl flex-shrink-0 text-red-500" />
              <div className="min-w-0">
                <p className="text-xs text-gray-600 font-medium">Vencidos</p>
                <p className="text-xl font-bold text-red-600">
                  {kpis.productosExpired.expiredProducts}
                </p>
                <p className="text-xs text-gray-500">
                  {kpis.productosExpired.expiringIn15Days} por vencer
                </p>
              </div>
            </div>

            {/* Días de suscripción */}
            {session?.hasPremiumAccess && (() => {
              const dias = suscripcion?.diasRestantes ?? null;
              const activo = suscripcion?.activo ?? true;
              const isUrgent = activo && dias !== null && dias <= 3;
              const isWarning = activo && dias !== null && dias <= 7 && !isUrgent;
              const color = !activo ? '#6B7280' : isUrgent ? '#EF4444' : isWarning ? '#F97316' : '#22C55E';
              const bg = !activo ? '#F3F4F6' : isUrgent ? '#FEF2F2' : isWarning ? '#FFF7ED' : '#DCFCE7';
              return (
                <div className="flex items-center gap-3 p-3 rounded-lg w-full md:w-auto md:min-w-[180px]" style={{ backgroundColor: bg, maxWidth: '250px' }}>
                  <MdCreditCard className="text-3xl flex-shrink-0" style={{ color }} />
                  <div className="min-w-0">
                    <p className="text-xs text-gray-600 font-medium">Suscripción</p>
                    <p className="text-xl font-bold" style={{ color: '#11194F' }}>
                      {dias !== null ? `${dias} día${dias === 1 ? '' : 's'}` : 'Activa'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {dias === null ? 'Al día' : isUrgent ? '¡Vence pronto!' : isWarning ? 'Por vencer' : 'Restantes'}
                    </p>
                  </div>
                </div>
              );
            })()}

            {/* Alerta SII */}
            {kpis.siiAlertKpi.show && (
              <div className="flex items-start gap-3 p-3 rounded-lg w-full md:w-auto md:min-w-[180px]" style={{ backgroundColor: '#FFF7ED', maxWidth: '250px' }}>
                <span className="text-3xl flex-shrink-0">⚠️</span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-orange-600 font-medium mb-1">Alerta SII</p>
                  <div className="space-y-1">
                    {kpis.siiAlertKpi.certExpiringSoon && kpis.siiAlertKpi.certDaysLeft > 0 && (
                      <p className="text-xs text-orange-700">
                        Certificado vence en <span className="font-semibold">{kpis.siiAlertKpi.certDaysLeft} días</span>
                      </p>
                    )} 
                    {kpis.siiAlertKpi.certExpiringSoon && kpis.siiAlertKpi.certDaysLeft <= 0 && (
                      <p className="text-xs text-orange-700">
                        El certificado ya vencio
                      </p>
                    )}
                    {kpis.siiAlertKpi.foliosExpiringSoon && kpis.siiAlertKpi.foliosDaysLeft > 0 && (
                      <p className="text-xs text-orange-700">
                        Folios vencen en <span className="font-semibold">{kpis.siiAlertKpi.foliosDaysLeft} días</span>
                      </p>
                    )}
                    {kpis.siiAlertKpi.foliosExpiringSoon && kpis.siiAlertKpi.foliosDaysLeft <= 0 && (
                      <p className="text-xs text-orange-700">
                        Los Folios ya vencieron
                      </p>
                    )}
                    {kpis.siiAlertKpi.isOutOfFolios && (
                      <p className="text-xs text-orange-700 font-semibold">
                        Sin folios disponibles
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
                </>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      )}

      <AnnouncementManager />
      <ProspectoPremiumAnuncio />

      {/* Módulos Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-6">
        {modules.map((module) => {
          const Icon = module.icon;
          const isMisiones = module.name === 'Misiones';
          const isSesionAyuda = module.name === 'Sesión de ayuda';
          const isPremium = module.name === 'Premium';
          const isMiNegocioPro = module.name === 'Mi Negocio Pro';
          const isCaja360 = module.name === 'Caja 360º';
          
          const CardContent = (
            <div
              className={`rounded-xl p-4 md:p-6 border transition-all duration-300 relative overflow-hidden ${
                isMisiones || isSesionAyuda || isPremium || isMiNegocioPro
                  ? 'hover:scale-105 h-32 md:h-44 lg:h-48' 
                  : 'hover:shadow-lg hover:scale-105 h-24 md:h-36 lg:h-40'
              }`}
              style={{ 
                background: isMisiones 
                  ? 'linear-gradient(90deg, #FF7B00 0%, #FFB800 100%)' 
                  : isSesionAyuda
                  ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
                  : isPremium
                  ? 'linear-gradient(135deg, #A855F7 0%, #EC4899 100%)'
                  : isMiNegocioPro
                  ? 'linear-gradient(135deg, #3B82F6 0%, #A855F7 100%)'
                  : '#FFFFFF',
                borderColor: isMisiones || isSesionAyuda || isPremium || isMiNegocioPro ? 'transparent' : '#E5E7EB',
                ...(isMisiones && {
                  boxShadow: '0 4px 6px rgba(255, 123, 0, 0.2)',
                }),
                ...(isSesionAyuda && {
                  boxShadow: '0 4px 6px rgba(16, 185, 129, 0.3)',
                }),
                ...(isPremium && {
                  boxShadow: '0 4px 6px rgba(168, 85, 247, 0.3)',
                }),
                ...(isMiNegocioPro && {
                  boxShadow: '0 6px 12px rgba(139, 92, 246, 0.4)',
                })
              }}
              onMouseEnter={(e) => {
                if (isMisiones) {
                  e.currentTarget.style.background = 'linear-gradient(90deg, #FFB800 0%, #FF7B00 100%)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(255, 123, 0, 0.4)';
                } else if (isSesionAyuda) {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #059669 0%, #047857 100%)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(16, 185, 129, 0.5)';
                } else if (isPremium) {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #9333EA 0%, #DB2777 100%)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(168, 85, 247, 0.5)';
                } else if (isMiNegocioPro) {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #2563EB 0%, #9333EA 100%)';
                  e.currentTarget.style.boxShadow = '0 10px 30px rgba(139, 92, 246, 0.6)';
                } else {
                  e.currentTarget.style.background = 'linear-gradient(#11194f, #262d6d)';
                  e.currentTarget.style.borderColor = 'transparent';
                  const h3 = e.currentTarget.querySelector('h3');
                  const icon = e.currentTarget.querySelector('svg');
                  if (h3) h3.style.color = '#FFFFFF';
                  if (icon) icon.style.color = '#FFD400';
                }
              }}
              onMouseLeave={(e) => {
                if (isMisiones) {
                  e.currentTarget.style.background = 'linear-gradient(90deg, #FF7B00 0%, #FFB800 100%)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(255, 123, 0, 0.2)';
                } else if (isSesionAyuda) {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #10B981 0%, #059669 100%)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(16, 185, 129, 0.3)';
                } else if (isPremium) {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #A855F7 0%, #EC4899 100%)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(168, 85, 247, 0.3)';
                } else if (isMiNegocioPro) {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #3B82F6 0%, #A855F7 100%)';
                  e.currentTarget.style.boxShadow = '0 6px 12px rgba(139, 92, 246, 0.4)';
                } else {
                  e.currentTarget.style.background = '#FFFFFF';
                  e.currentTarget.style.borderColor = '#E5E7EB';
                  const h3 = e.currentTarget.querySelector('h3');
                  const icon = e.currentTarget.querySelector('svg');
                  if (h3) h3.style.color = '#11194F';
                  if (icon) icon.style.color = module.iconColor;
                }
              }}
            >
              {/* Badges superiores */}
              {isCaja360 && (
                /* Badge NUEVO diagonal */
                <div className="absolute -top-5 -left-5 overflow-hidden" style={{ width: '90px', height: '90px' }}>
                  <div
                    className="absolute top-0 left-0 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs font-bold flex items-center justify-center shadow-lg"
                    style={{
                      width: '120px',
                      height: '32px',
                      transform: 'rotate(-45deg) translate(-30px, 0px)',
                      transformOrigin: 'center',
                    }}
                  >
                    NUEVO
                  </div>
                </div>
              )}
              {isMiNegocioPro && (
                /* Badge PRO */
                <div className="absolute top-2 right-2 md:top-3 md:right-3 bg-white/90 backdrop-blur-sm rounded-md md:rounded-lg px-1.5 py-0.5 md:px-3 md:py-1.5 flex items-center gap-0.5 md:gap-1 shadow-md">
                  <span className="text-purple-600 text-sm md:text-lg">✦</span>
                  <span className="text-purple-600 font-bold text-[10px] md:text-sm">PRO</span>
                </div>
              )}
              {isPremium && (
                <div className="absolute top-2 right-2 md:top-3 md:right-3 rounded-md md:rounded-lg px-1.5 py-0.5 md:px-3 md:py-1.5 flex items-center gap-0.5 md:gap-1 shadow-md bg-yellow-400" >
                  <span className="text-purple-700 text-sm md:text-lg">✦</span>
                  <span className="text-purple-700 font-bold text-[10px] md:text-sm">PREMIUM</span>
                </div>
              )}

              <div className="flex items-start justify-between h-full">
                <div className={`flex flex-col h-full ${(isMiNegocioPro || isPremium) ? 'pt-10 pb-2 justify-between' : (isMisiones || isSesionAyuda) ? 'justify-center' : 'py-2 justify-between'}`}>
                  <div>
                    <h3
                      className="font-bold text-lg md:text-xl lg:text-2xl xl:text-3xl leading-tight mb-2"
                      style={{ 
                        color: isMisiones || isSesionAyuda || isPremium || isMiNegocioPro ? '#FFFFFF' : '#11194F'
                      }}
                    >
                      {module.name}
                    </h3>
                    {(isMiNegocioPro || isPremium) && (
                      <p className="text-white/90 text-xs md:text-sm lg:text-base font-medium leading-snug">
                        {isMiNegocioPro ? 'Visualiza el rendimiento de tu negocio.' : 'Desbloquea funciones avanzadas.'}
                      </p>
                    )}
                  </div>
                </div>
                <div className={`relative flex-shrink-0 ml-4 ${(isMiNegocioPro || isPremium) ? 'pt-8' : (isMisiones || isSesionAyuda) ? 'self-center' : ''}`}>
                  <Icon 
                    className={`${(isMiNegocioPro || isPremium) ? 'text-4xl md:text-5xl lg:text-6xl xl:text-7xl' : 'text-3xl md:text-4xl lg:text-5xl xl:text-5xl'}`}
                    style={{ color: isMisiones || isSesionAyuda || isPremium || isMiNegocioPro ? '#FFFFFF' : module.iconColor }}
                  />
                  {isPremium && (
                    <>
                      <span className="absolute top-14 -left-4 text-yellow-400 text-xl">✦</span>
                      <span className="absolute top-14 -right-4 text-yellow-400 text-xl">✦</span>
                      <span className="absolute bottom-2 -right-2 text-yellow-400 text-sm">✦</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          );

          if (isMisiones) {
            return (
              <div
                key={module.name}
                onClick={() => setShowMisionesModal(true)}
                className="cursor-pointer"
              >
                {CardContent}
              </div>
            );
          }

          // Premium: en desktop abre URL externa, en web navega internamente
          if (module.desktopExternalUrl && window.electronAPI) {
            return (
              <div
                key={module.name}
                onClick={() => openExternalLink(module.desktopExternalUrl)}
                className="group cursor-pointer"
              >
                {CardContent}
              </div>
            );
          }
          
          if (module.isExternalLink) {
            if (window.electronAPI) {
              return (
                <div
                  key={module.name}
                  onClick={() => openExternalLink(module.href)}
                  className="group cursor-pointer"
                >
                  {CardContent}
                </div>
              );
            }

            return (
              <a
                key={module.name}
                href={module.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group"
              >
                {CardContent}
              </a>
            );
          }
          
          return (
            <Link
              key={module.name}
              to={module.href}
              className="group"
            >
              {CardContent}
            </Link>
          );
        })}
      </div>

      {/* Modal de Misiones */}
      {showMisionesModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowMisionesModal(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8 relative"
            onClick={(e) => e.stopPropagation()}
            style={{ borderLeft: '8px solid #FF7B00' }}
          >
            {/* Botón cerrar */}
            <button
              onClick={() => setShowMisionesModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Contenido del modal */}
            <div className="text-center">
              <h2 className="text-4xl font-bold mb-4" style={{ color: '#11194F' }}>
                👀 Algo grande se viene...
              </h2>
              <p className="text-2xl mb-8 text-gray-700">
                Misiones para ganar premios para ti y tu negocio 🚀
              </p>
              <style>{`
                @keyframes scaleUp {
                  0%, 100% {
                    transform: scale(1);
                  }
                  50% {
                    transform: scale(1.05);
                  }
                }
              `}</style>
              <button
                className="text-white font-bold py-4 px-8 rounded-xl text-xl"
                style={{ 
                  background: 'linear-gradient(90deg, #FF7B00 0%, #FFB800 100%)',
                  animation: 'scaleUp 1.5s ease-in-out infinite'
                }}
              >
                Muy pronto disponible
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
